import { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { useEffect, useRef } from 'react';
import { mixin } from '@/mixin';
import { SessionsMixin } from './sessions';
import { PatientsMixin } from './patients';
import { ConversationsMixin } from './conversations';
import { PatientNotesMixin } from './patientNotes';
import { SessionNotesMixin } from './sessionNotes';
import { UtilsMixin } from './utils';
import { ReportsMixin } from './reports';
import { TemplatesMixin } from './templates';
import { SectionsMixin } from './sections';
import { AgendaConfigurationMixin } from './agendaConfiguration';
import { BookingMixin } from './booking';
import { UserMixin } from './users';
import { OAuth2Mixin } from './oauth2';
import { ReminderMixin } from './reminder';
import { PatientReminderMixin } from './patient_reminder';
import { EmailLogsMixin } from './email_logs';
import { BookingCheckoutMixin } from './bookingCheckout';
import { ActionsMixin } from './actions';
import { useTranslation } from 'react-i18next';
import { RecurrenceMixin } from './recurrence';
import { useTimezone } from "@/contexts/TimezoneContext";
import { DateTime } from 'luxon';

export const baseUrl = `${import.meta.env.VITE_API_URL}/v1`;

export class ApiError extends Error {
  status!: string;
  errors!: string | any[];
  constructor(message?: string, err?: Record<string, any>) {
    super(...arguments);
    if (err instanceof Object && err.errors != null) {
      Object.assign(this, err);
    }
  }
}

export interface GetAccessTokenSilently {
  (
    options: GetTokenSilentlyOptions & { detailedResponse: true }
  ): Promise<GetTokenSilentlyVerboseResponse>;
  (options?: GetTokenSilentlyOptions): Promise<string>;
  (options: GetTokenSilentlyOptions): Promise<GetTokenSilentlyVerboseResponse | string>;
}

export class Api extends mixin(
  SessionsMixin,
  PatientsMixin,
  ConversationsMixin,
  PatientNotesMixin,
  SessionNotesMixin,
  UtilsMixin,
  ReportsMixin,
  TemplatesMixin,
  SectionsMixin,
  AgendaConfigurationMixin,
  OAuth2Mixin,
  BookingMixin,
  UserMixin,
  ReminderMixin,
  PatientReminderMixin,
  EmailLogsMixin,
  BookingCheckoutMixin,
  ActionsMixin,
  RecurrenceMixin
) {
  getAccessTokenSilently: GetAccessTokenSilently | undefined;
  clientTimezone?: string;
  clientLocale?: string;

  constructor({
    getAccessTokenSilently,
    clientTimezone,
    clientLocale,
  }: {
    getAccessTokenSilently?: GetAccessTokenSilently;
    clientTimezone?: string;
    clientLocale?: string;
  }) {
    super();
    this.getAccessTokenSilently = getAccessTokenSilently;
    this.clientTimezone = clientTimezone;
    this.clientLocale = clientLocale;
  }

  setTimezone(timezone: string): void {
    this.clientTimezone = timezone;
  }
  setLocale(clientLocale: string): void {
    this.clientLocale = clientLocale;
  }

  fetch = async (path: string, init?: RequestInit): Promise<any> => {
    const token = this.getAccessTokenSilently ? await this.getAccessTokenSilently() : null;
    const authObject: any = token ? { Authorization: `Bearer ${token}` } : {};
    const localeQuery = this.clientLocale ? `&locale=${this.clientLocale}` : "";
    const timezoneParamQuery = this.clientTimezone ? `&client_time_zone_param=${this.clientTimezone}` : "";

    const res = await fetch(
      `${baseUrl + path + (path.includes('?') ? '&' : '?')}time_zone=${DateTime.local().zoneName}${localeQuery}${timezoneParamQuery}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...authObject,
        },
        ...init,
      }
    );

    if (res.ok) {
      try {
        return await res.json();
      } catch (e) {
        if (e instanceof SyntaxError) {
          return undefined;
        }
        throw e;
      }
    } else {
      try {
        const err = await res.json();
        throw new ApiError(
          err.errors.map((err: { message: string }) => err.message).join('\n'),
          err
        );
      } catch (e) {
        if (e instanceof SyntaxError) {
          throw new ApiError(`Received ${res.status} ${res.statusText}`);
        } else {
          throw e;
        }
      }
    }
  };
}

export const useApi = ({
  getAccessTokenSilently,
  timezone,
  locale
}: {
  getAccessTokenSilently?: GetAccessTokenSilently;
  timezone?: string;
  locale?: string;
}) => useRef(new Api({ getAccessTokenSilently, clientTimezone: timezone, clientLocale: locale })).current;

export const useEloApi = ({
  getAccessTokenSilently,
}: {
  getAccessTokenSilently?: GetAccessTokenSilently;
}) => {
  const { i18n } = useTranslation();
  const { timezone } = useTimezone();
  const api = useApi({getAccessTokenSilently: getAccessTokenSilently, timezone: timezone, locale: i18n.language});

  useEffect(() => {
    if (i18n.language !== api.clientLocale) api.setLocale(i18n.language);
    if (timezone !== api.clientTimezone) api.setTimezone(timezone);
  }, [i18n.language, timezone]);

  return api;
};